import { useEffect, useMemo, useRef, useState } from 'react';
import { archetypeWpmHint, GOAL_ARCHETYPES } from '../data/goalArchetypes';
import { KEYBOARD_LAYOUT_LABELS } from '../data/keyboardLayouts';
import { getDictationAudio, type DictationVoice } from '../lib/audio';
import { dictationUnitLengthLabel } from '../lib/dictationUnits';
import { goalsFromArchetype } from '../lib/goals';
import Niveau4LexiconToggle from './Niveau4LexiconToggle';
import DictationLexiconToggle from './DictationLexiconToggle';
import type {
  GoalArchetypeId,
  KeyboardLayoutId,
  UserGoals,
  UserPreferences,
} from '../types';

const LAYOUT_IDS = ['mac-dk', 'pc-dk'] as const;

interface SettingsMenuProps {
  keyboardLayout: KeyboardLayoutId;
  onKeyboardLayoutChange: (layout: KeyboardLayoutId) => void;
  preferences: UserPreferences;
  onPreferencesChange: (patch: Partial<UserPreferences>) => void;
  goals?: UserGoals;
  onGoalsChange: (patch: Partial<UserGoals>) => void;
  onRetestBaseline: (kind: 'transcription' | 'composition') => void;
}

export default function SettingsMenu({
  keyboardLayout,
  onKeyboardLayoutChange,
  preferences,
  onPreferencesChange,
  goals,
  onGoalsChange,
  onRetestBaseline,
}: SettingsMenuProps) {
  const [open, setOpen] = useState(false);
  const [advancedGoals, setAdvancedGoals] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Indstillinger"
        title="Indstillinger"
        className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white text-zinc-700 ring-1 ring-zinc-200 transition hover:bg-zinc-50 dark:bg-zinc-800 dark:text-zinc-200 dark:ring-white/10 dark:hover:bg-zinc-700"
      >
        <GearIcon />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-20 mt-2 max-h-[min(32rem,85vh)] w-80 overflow-y-auto rounded-xl bg-white p-3 shadow-lg ring-1 ring-zinc-200 dark:bg-zinc-900 dark:ring-white/10 dark:shadow-black/40"
        >
          <p className="label">Tastatur</p>
          <p className="mt-0.5 text-xs text-faint">
            Layout til tastaturvisning i lektioner
          </p>
          <ul className="mt-2 space-y-1" role="none">
            {LAYOUT_IDS.map((id) => {
              const meta = KEYBOARD_LAYOUT_LABELS[id];
              const active = keyboardLayout === id;
              return (
                <li key={id} role="none">
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={active}
                    onClick={() => {
                      onKeyboardLayoutChange(id);
                    }}
                    className={`w-full rounded-lg px-3 py-2 text-left text-sm transition ${
                      active
                        ? 'bg-indigo-50 text-strong ring-1 ring-indigo-200 dark:bg-indigo-500/10 dark:ring-indigo-500/40'
                        : 'text-muted hover:bg-zinc-50 dark:hover:bg-zinc-800/60'
                    }`}
                  >
                    <span className="font-medium">{meta.label}</span>
                    <span className="mt-0.5 block text-xs text-faint">
                      {meta.description}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>

          <div className="my-3 h-px bg-zinc-200 dark:bg-white/5" />

          {goals && (
            <>
              <GoalsSection
                goals={goals}
                advanced={advancedGoals}
                onToggleAdvanced={() => setAdvancedGoals((v) => !v)}
                onGoalsChange={onGoalsChange}
              />
              <div className="my-3 h-px bg-zinc-200 dark:bg-white/5" />
            </>
          )}

          <BaselineSection
            onRetest={(kind) => {
              setOpen(false);
              onRetestBaseline(kind);
            }}
          />

          <div className="my-3 h-px bg-zinc-200 dark:bg-white/5" />

          <Niveau4LexiconToggle
            compact
            value={preferences.niveau4Lexicon ?? 'da'}
            onChange={(niveau4Lexicon) => onPreferencesChange({ niveau4Lexicon })}
          />

          <div className="my-3 h-px bg-zinc-200 dark:bg-white/5" />

          <DictationLexiconToggle
            compact
            value={preferences.dictationLexicon ?? 'da'}
            onChange={(dictationLexicon) =>
              onPreferencesChange({ dictationLexicon })
            }
          />

          <div className="my-3 h-px bg-zinc-200 dark:bg-white/5" />

          <p className="label">Orddiktat</p>
          <p className="mt-0.5 text-xs text-faint">Enhedslængde for ekstra øvelse</p>
          <label className="mt-2 block text-xs text-strong">
            {dictationUnitLengthLabel(preferences.dictationUnitLength ?? 1)} ord
            <input
              type="range"
              min={1}
              max={7}
              step={1}
              value={preferences.dictationUnitLength ?? 1}
              onChange={(e) =>
                onPreferencesChange({
                  dictationUnitLength: Number(e.target.value),
                })
              }
              className="mt-1 w-full accent-indigo-500"
            />
          </label>

          <div className="my-3 h-px bg-zinc-200 dark:bg-white/5" />

          <AudioAndHelpSection
            preferences={preferences}
            onPreferencesChange={onPreferencesChange}
          />
        </div>
      )}
    </div>
  );
}

function GoalsSection({
  goals,
  advanced,
  onToggleAdvanced,
  onGoalsChange,
}: {
  goals: UserGoals;
  advanced: boolean;
  onToggleAdvanced: () => void;
  onGoalsChange: (patch: Partial<UserGoals>) => void;
}) {
  return (
    <div className="space-y-2">
      <p className="label">Dit mål</p>
      <select
        value={goals.archetypeId}
        onChange={(e) => {
          const id = e.target.value as GoalArchetypeId;
          onGoalsChange(goalsFromArchetype(id));
        }}
        className="mt-1 block w-full rounded-md border border-zinc-200 bg-white px-2 py-1.5 text-sm dark:border-white/10 dark:bg-zinc-800"
      >
        {GOAL_ARCHETYPES.map((a) => (
          <option key={a.id} value={a.id}>
            {a.title} ({archetypeWpmHint(a.id)})
          </option>
        ))}
      </select>
      <p className="text-xs text-faint">
        Mål: {goals.targetWpm} wpm · {(goals.targetAccuracy * 100).toFixed(0)}%
        · {Math.round(goals.targetCurriculumProgress * 100)}% kurriculum
      </p>
      <button
        type="button"
        onClick={onToggleAdvanced}
        className="text-xs text-indigo-600 dark:text-indigo-400"
      >
        {advanced ? 'Skjul avanceret' : 'Avanceret justering'}
      </button>
      {advanced && (
        <div className="space-y-2 rounded-lg bg-zinc-50 p-2 dark:bg-zinc-800/50">
          <label className="block text-xs">
            WPM-mål: {goals.targetWpm}
            <input
              type="range"
              min={15}
              max={120}
              value={goals.targetWpm}
              onChange={(e) =>
                onGoalsChange({ targetWpm: Number(e.target.value) })
              }
              className="mt-1 w-full"
            />
          </label>
          <label className="block text-xs">
            Præcision: {(goals.targetAccuracy * 100).toFixed(0)}%
            <input
              type="range"
              min={80}
              max={100}
              value={Math.round(goals.targetAccuracy * 100)}
              onChange={(e) =>
                onGoalsChange({
                  targetAccuracy: Number(e.target.value) / 100,
                })
              }
              className="mt-1 w-full"
            />
          </label>
          <label className="block text-xs">
            Kurriculum: {Math.round(goals.targetCurriculumProgress * 100)}%
            <input
              type="range"
              min={10}
              max={100}
              value={Math.round(goals.targetCurriculumProgress * 100)}
              onChange={(e) =>
                onGoalsChange({
                  targetCurriculumProgress: Number(e.target.value) / 100,
                })
              }
              className="mt-1 w-full"
            />
          </label>
        </div>
      )}
    </div>
  );
}

function BaselineSection({
  onRetest,
}: {
  onRetest: (kind: 'transcription' | 'composition') => void;
}) {
  return (
    <div className="space-y-2">
      <p className="label">Udgangspunkt</p>
      <p className="text-xs text-faint">
        Gentest opdaterer dit gemte udgangspunkt (ikke historik).
      </p>
      <button
        type="button"
        onClick={() => onRetest('transcription')}
        className="w-full rounded-lg bg-zinc-100 px-3 py-2 text-xs font-medium text-strong hover:bg-zinc-200 dark:bg-zinc-800 dark:hover:bg-zinc-700"
      >
        Gentag afskriftstest
      </button>
      <button
        type="button"
        onClick={() => onRetest('composition')}
        className="w-full rounded-lg bg-zinc-100 px-3 py-2 text-xs font-medium text-strong hover:bg-zinc-200 dark:bg-zinc-800 dark:hover:bg-zinc-700"
      >
        Gentag hverdagstest
      </button>
    </div>
  );
}

function AudioAndHelpSection({
  preferences,
  onPreferencesChange,
}: {
  preferences: UserPreferences;
  onPreferencesChange: (patch: Partial<UserPreferences>) => void;
}) {
  const audio = useMemo(() => getDictationAudio(), []);
  const [voices, setVoices] = useState<DictationVoice[]>([]);
  const [voicesLoaded, setVoicesLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void audio.waitForVoices().then(() => {
      if (cancelled) return;
      setVoices(audio.listDanishVoices());
      setVoicesLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [audio]);

  const testVoice = () => {
    audio.cancel();
    void audio.speak('Hej. Skriv det jeg siger.', {
      rate: preferences.dictationRate,
      voiceId: preferences.dictationVoiceId,
    });
  };

  return (
    <div className="space-y-3">
      <p className="label">Lyd og hjælp</p>

      <ToggleRow
        label="Lyd"
        sub="Tillad oplæsning og metronom"
        checked={preferences.audioEnabled}
        onChange={(v) => onPreferencesChange({ audioEnabled: v })}
      />

      <ToggleRow
        label="Mental forberedelse"
        sub="15 sek visning af nye taster før første runde"
        checked={preferences.mentalRehearsalEnabled}
        onChange={(v) => onPreferencesChange({ mentalRehearsalEnabled: v })}
      />

      <ToggleRow
        label="Metronom-lyd"
        sub="Tikkende lyd i rytme-runder (ellers kun visuelt)"
        checked={preferences.metronomeEnabled}
        onChange={(v) => onPreferencesChange({ metronomeEnabled: v })}
        disabled={!preferences.audioEnabled}
      />

      <div>
        <label
          htmlFor="dictation-voice"
          className="block text-xs font-medium text-strong"
        >
          Diktat-stemme
        </label>
        {!voicesLoaded ? (
          <p className="mt-1 text-xs text-faint">Indlæser stemmer…</p>
        ) : voices.length === 0 ? (
          <p className="mt-1 text-xs text-faint">
            Ingen dansk stemme fundet på din enhed. Diktat-runder vil vise
            teksten i stedet.
          </p>
        ) : (
          <select
            id="dictation-voice"
            value={preferences.dictationVoiceId ?? ''}
            onChange={(e) =>
              onPreferencesChange({
                dictationVoiceId: e.target.value === '' ? null : e.target.value,
              })
            }
            disabled={!preferences.audioEnabled}
            className="mt-1 block w-full rounded-md border border-zinc-200 bg-white px-2 py-1.5 text-sm text-strong disabled:opacity-50 dark:border-white/10 dark:bg-zinc-800"
          >
            <option value="">Auto (første danske)</option>
            {voices.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
                {v.localService ? '' : ' (online)'}
              </option>
            ))}
          </select>
        )}
      </div>

      <div>
        <label
          htmlFor="dictation-rate"
          className="block text-xs font-medium text-strong"
        >
          Oplæsningshastighed:{' '}
          <span className="font-mono">{preferences.dictationRate.toFixed(2)}×</span>
        </label>
        <input
          id="dictation-rate"
          type="range"
          min={0.6}
          max={1.2}
          step={0.05}
          value={preferences.dictationRate}
          onChange={(e) =>
            onPreferencesChange({ dictationRate: Number(e.target.value) })
          }
          disabled={!preferences.audioEnabled}
          className="mt-1 w-full disabled:opacity-50"
        />
        <p className="text-xs text-faint">
          Lavere = langsommere. Anbefalet for begyndere: 0.80–0.90.
        </p>
      </div>

      <button
        type="button"
        onClick={testVoice}
        disabled={!preferences.audioEnabled || voices.length === 0}
        className="w-full rounded-lg bg-indigo-500 px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-indigo-400 disabled:cursor-not-allowed disabled:opacity-50"
      >
        Test stemme
      </button>
    </div>
  );
}

function ToggleRow({
  label,
  sub,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  sub: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label
      className={`flex items-start justify-between gap-3 ${
        disabled ? 'opacity-50' : ''
      }`}
    >
      <span className="flex-1 min-w-0">
        <span className="block text-xs font-medium text-strong">{label}</span>
        <span className="block text-xs text-faint">{sub}</span>
      </span>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-indigo-500"
      />
    </label>
  );
}

function GearIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden="true"
    >
      <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}
